import React, { useState, useEffect, useRef } from "react";
import "../styles/components/WaveformDisplay.css";
import { MicrophoneImpl, WaveformCallback, KeyType } from "../utils/MicrophoneImpl";

interface WaveformDisplayProps {
  // No props needed for now
}

export const WaveformDisplay: React.FC<WaveformDisplayProps> = () => {
  // State to keep track of the chiclet heights (min 10px, max 32px)
  const [chicletHeights, setChicletHeights] = useState<number[]>(Array(4).fill(10));
  const microphoneRef = useRef<MicrophoneImpl | null>(null);

  // Buffer to accumulate audio data
  const audioBufferRef = useRef<Int16Array[]>([]);
  // Threshold for buffer size before processing
  const BUFFER_THRESHOLD = 384;

  // Function to calculate decibel level from audio data
  // const calculateDecibelLevel = (audioData: Int16Array): number => {
  //   // Calculate RMS (Root Mean Square) of the audio data
  //   let sumOfSquares = 0;
  //   const sampleCount = Math.min(audioData.length, 1024); // Limit to prevent performance issues

  //   for (let i = 0; i < sampleCount; i++) {
  //     sumOfSquares += audioData[i] * audioData[i];
  //   }

  //   const rms = Math.sqrt(sumOfSquares / sampleCount);

  //   // Convert to a value between 0 and 1, then scale to our height range
  //   // Int16Array values range from -32768 to 32767
  //   const normalizedLevel = Math.min(1, rms / 10000); // Adjust divisor for sensitivity

  //   // Scale between min height (10px) and max height (32px)
  //   return 10 + normalizedLevel * 22; // 10px min + scaled portion up to 22px more
  // };

  // Function to calculate aggregate level from multiple audio samples
  const calculateAggregateLevel = (audioBuffers: Int16Array[]): number => {
    // Combine all buffers into one large calculation
    let totalSumOfSquares = 0;
    let totalSamples = 0;

    for (const buffer of audioBuffers) {
      const sampleCount = Math.min(buffer.length, 1024);
      for (let i = 0; i < sampleCount; i++) {
        totalSumOfSquares += buffer[i] * buffer[i];
      }
      totalSamples += sampleCount;
    }

    // Calculate RMS across all samples
    const aggregateRms = Math.sqrt(totalSumOfSquares / (totalSamples || 1));

    // Normalize and scale
    const normalizedLevel = Math.min(1, aggregateRms / 5000);

    return 10 + normalizedLevel * 22;
  };

  useEffect(() => {
    // Get the singleton instance of MicrophoneImpl
    microphoneRef.current = MicrophoneImpl.getInstance();

    // Create our waveform callback
    const handleWaveformData: WaveformCallback = (
      data: Int16Array,
      isFirst: boolean,
      isLast: boolean,
      keyType: KeyType
    ) => {
      // Add the new data to our buffer
      audioBufferRef.current.push(data);

      // Check if we've accumulated enough data
      let bufferSize = 0;
      for (const buffer of audioBufferRef.current) {
        bufferSize += buffer.length;
      }

      if (bufferSize >= BUFFER_THRESHOLD) {
        // Calculate aggregate level from the accumulated data
        const aggregateLevel = calculateAggregateLevel(audioBufferRef.current);

        // Update chiclet heights by shifting left and adding new level at the end
        setChicletHeights((prevHeights) => {
          const newHeights = [...prevHeights];
          // Shift all values left by one position
          for (let i = 0; i < newHeights.length - 1; i++) {
            newHeights[i] = newHeights[i + 1];
          }
          // Add new value at the end
          newHeights[newHeights.length - 1] = aggregateLevel;
          return newHeights;
        });

        // Clear the buffer
        audioBufferRef.current = [];
      }
    };

    // Register the callback
    if (microphoneRef.current) {
      microphoneRef.current.addWaveformCallback("wvd", handleWaveformData);
      //microphoneRef.current.addWaveformTap(handleWaveformData);
    }

    // Cleanup function to remove the callback when component unmounts
    return () => {
      if (microphoneRef.current) {
        //microphoneRef.current.removeWaveformTap(handleWaveformData);
        microphoneRef.current.removeWaveformCallback("wvd");
      }
    };
  }, []);

  return (
    <div className="waveform-container">
      {chicletHeights.map((height, index) => (
        <div key={index} className="waveform-chiclet" style={{ height: `${height}px` }} />
      ))}
    </div>
  );
};

export default WaveformDisplay;
